import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bot, Plus, Pencil, Trash2, RefreshCw, X, Save, Sparkles, Wand2, Dice5,
  Crown, Brain, FileText,
} from "lucide-react";
import { toast } from "sonner";
import TranslatedInput from "@/components/admin/TranslatedInput";

const DEFAULT_INTRO = {
  pt: "Cada robot é avaliado em três parâmetros: rapidez de resposta, percentagem de respostas corretas e força mental. Vence um robot e ganha pontos para o ranking — tantos quanto o dobro da sua força mental! Mas cuidado! Se perderes, serão descontados pontos correspondentes a metade da força mental do robô que te derrotou.",
  en: "Our robots are rated based on three parameters: response speed, accuracy rate, and mental strength. Every time you defeat a robot, you earn ranking points equal to twice its mental strength. But be careful! If you are defeated, you will lose points equal to half the mental strength of the robot that defeated you.",
  es: "Nuestros robots se clasifican según tres parámetros: velocidad de respuesta, porcentaje de respuestas correctas y fuerza mental. Cada vez que derrotas a un robot, ganas puntos para la clasificación equivalentes al doble de su fuerza mental. ¡Pero cuidado! Si eres derrotado, perderás puntos equivalentes a la mitad de la fuerza mental del robot que te haya vencido.",
};

// VS Robot only lists `training` games, but robots are ALSO used in
// eliminatórias — so the picker shows every mode. Games often share the
// same display name across modes (e.g. two "VOCABULÁRIO FRANCÊS"), which
// made admins assign a robot to the wrong duplicate and then not find it
// in VS Robot. We label each game with its mode so duplicates are clear.
const GAME_MODE_LABEL = {
  training: "treino",
  competition: "competição",
  eliminatorias: "eliminatórias",
  grande_final: "grande final",
};
const gameModeLabel = (s) => GAME_MODE_LABEL[s] || s || "—";

export default function AdminRobots() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | robot
  const [introOpen, setIntroOpen] = useState(false);
  const [introForm, setIntroForm] = useState({ pt: "", en: "", es: "" });
  const [introSaving, setIntroSaving] = useState(false);

  const openIntro = async () => {
    try {
      const { data } = await api.get("/vs-robot/admin/config");
      setIntroForm({
        pt: data.intro_pt || "",
        en: data.intro_en || "",
        es: data.intro_es || "",
      });
      setIntroOpen(true);
    } catch {
      toast.error("Erro a carregar texto introdutório");
    }
  };

  const saveIntro = async () => {
    setIntroSaving(true);
    try {
      await api.put("/vs-robot/admin/config", {
        intro_pt: introForm.pt || "",
        intro_en: introForm.en || "",
        intro_es: introForm.es || "",
      });
      toast.success("Texto introdutório guardado");
      setIntroOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally { setIntroSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/robots"),
        api.get("/games"),
      ]);
      setRows(r1.data || []);
      setGames(r2.data || []);
    } catch (e) {
      toast.error("Erro a carregar robots");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Apagar o robot "${name}"?`)) return;
    try {
      await api.delete(`/robots/${id}`);
      toast.success("Robot apagado");
      load();
    } catch (e) { toast.error("Erro a apagar"); }
  };

  const seedRandom = async () => {
    try {
      const { data } = await api.post("/robots/seed-random", null, { params: { count: 3 } });
      toast.success(`${data.created} robots gerados`);
      load();
    } catch (e) { toast.error("Falha ao gerar"); }
  };

  return (
    <div data-testid="admin-robots">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-sky-500 text-white p-2.5 shadow-md">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900 leading-tight">Robots</h1>
            <p className="text-sm text-slate-500">Adversários virtuais do modo VS ROBOT — configura imagem, eficácia e rapidez.</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openIntro} variant="outline" className="rounded-full border-sky-400 text-sky-700 hover:bg-sky-50" data-testid="robots-edit-intro">
            <FileText className="w-4 h-4 mr-1.5" /> Editar texto introdutório
          </Button>
          <Button onClick={() => nav("/admin/utilizadores-vs-robot")} variant="outline" className="rounded-full border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="robots-top-config">
            <Crown className="w-4 h-4 mr-1.5" /> Editar TOP
          </Button>
          <Button onClick={load} variant="outline" className="rounded-full" data-testid="robots-reload">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Recarregar
          </Button>
          <Button onClick={seedRandom} variant="outline" className="rounded-full" data-testid="robots-seed-random">
            <Dice5 className="w-4 h-4 mr-1.5" /> Gerar 3 aleatórios
          </Button>
          <Button onClick={() => setEditing("new")} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid="robots-create">
            <Plus className="w-4 h-4 mr-1.5" /> Novo Robot
          </Button>
        </div>
      </div>

      {rows.length === 0 && !loading && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 text-amber-900" data-testid="robots-empty">
          <strong>Sem robots configurados.</strong>
          <p className="text-sm mt-1">Clica em <b>Novo Robot</b> ou <b>Gerar 3 aleatórios</b> para começares.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => {
          // Resolve list of game names (multi or legacy single)
          const robotGameIds = (r.game_ids && r.game_ids.length) ? r.game_ids : (r.game_id ? [r.game_id] : []);
          const gameNames = robotGameIds
            .map((gid) => {
              const g = games.find((x) => x.id === gid);
              return g ? `${g.name} · ${gameModeLabel(g.status)}` : null;
            })
            .filter(Boolean);
          const levels = r.levels || [];
          return (
            <div key={r.id} className={`rounded-2xl bg-white border-2 shadow-sm p-4 ${r.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`robot-card-${r.id}`}>
              <div className="flex items-start gap-3">
                <img src={r.image_url} alt={r.name} className="w-20 h-20 rounded-xl object-contain bg-slate-100 border border-slate-200 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-[Fredoka] font-bold text-slate-900 truncate">{r.name}</div>
                  {r.alcunha && <div className="text-xs text-slate-500 italic truncate">{r.alcunha}</div>}
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                      <Sparkles className="w-2.5 h-2.5" /> {r.efficacy}%
                    </span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-bold">
                      ⚡ {r.speed_min}–{r.speed_max}s
                    </span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold" title="Força mental">
                      <Brain className="w-2.5 h-2.5" /> {r.mental_strength ?? 5}/10
                    </span>
                    {!r.active && <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-bold">INATIVO</span>}
                  </div>
                  {gameNames.length > 0 && (
                    <div className="text-[10px] text-slate-400 mt-1 truncate" title={gameNames.join(", ")}>
                      Jogos: {gameNames.join(", ")}
                    </div>
                  )}
                  {levels.length > 0 && (
                    <div className="text-[10px] text-slate-400 truncate">
                      Níveis: {levels.sort().join(", ")}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-1 mt-3">
                <button onClick={() => setEditing(r)} className="p-1.5 text-sky-600 hover:bg-sky-50 rounded" data-testid={`robot-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(r.id, r.name)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`robot-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <RobotDialog
          robot={editing === "new" ? null : editing}
          games={games}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {introOpen && (
        <div
          className="fixed inset-0 z-[90] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setIntroOpen(false); }}
          data-testid="intro-edit-overlay"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-sky-600" />
                <h3 className="text-xl font-[Fredoka] font-bold text-slate-900">Texto introdutório — VS Robot</h3>
              </div>
              <button onClick={() => setIntroOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="intro-edit-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-slate-500">
              Este texto aparece na página pública <code>/vs-robot</code>, abaixo do título. Define uma versão para cada idioma. Deixa um campo vazio para usar o texto padrão (i18n) desse idioma.
            </p>

            {[
              { code: "pt", flag: "🇵🇹", label: "Português" },
              { code: "en", flag: "🇬🇧", label: "English" },
              { code: "es", flag: "🇪🇸", label: "Español" },
            ].map(({ code, flag, label }) => (
              <div key={code} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-1"><span>{flag}</span> {label}</Label>
                  <button
                    type="button"
                    onClick={() => setIntroForm((f) => ({ ...f, [code]: DEFAULT_INTRO[code] }))}
                    className="text-[11px] text-sky-600 hover:underline"
                    data-testid={`intro-edit-default-${code}`}
                  >
                    Repor texto padrão
                  </button>
                </div>
                <textarea
                  value={introForm[code]}
                  onChange={(e) => setIntroForm({ ...introForm, [code]: e.target.value })}
                  rows={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-[Nunito] focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder={`(vazio = usa o texto i18n padrão de ${label})`}
                  data-testid={`intro-edit-${code}`}
                />
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setIntroOpen(false)}>Cancelar</Button>
              <Button onClick={saveIntro} disabled={introSaving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="intro-edit-save">
                <Save className="w-4 h-4 mr-2" /> {introSaving ? "A guardar…" : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RobotDialog({ robot, games, onClose, onSaved }) {
  // Build initial game_ids: prefer new multi field, else legacy single, else empty.
  const initialGameIds = (robot?.game_ids && robot.game_ids.length)
    ? robot.game_ids
    : (robot?.game_id ? [robot.game_id] : []);
  const [form, setForm] = useState({
    name: robot?.name || "",
    alcunha: robot?.alcunha || "",
    image_url: robot?.image_url || "",
    image_seed: robot?.image_seed || "",
    efficacy: robot?.efficacy ?? 75,
    speed_min: robot?.speed_min ?? 1.5,
    speed_max: robot?.speed_max ?? 4.0,
    mental_strength: robot?.mental_strength ?? 5,
    game_ids: initialGameIds,
    levels: robot?.levels || [],
    active: robot?.active !== false,
    translations: robot?.translations || {},
  });
  const [candidates, setCandidates] = useState([]);
  const [loadingImgs, setLoadingImgs] = useState(false);
  const [saving, setSaving] = useState(false);

  const regenerate = async () => {
    setLoadingImgs(true);
    try {
      const { data } = await api.get("/robots/generate-images", { params: { count: 3 } });
      setCandidates(data || []);
    } catch (e) { toast.error("Falha ao gerar imagens"); }
    finally { setLoadingImgs(false); }
  };
  useEffect(() => { if (!robot) regenerate(); }, []); // eslint-disable-line

  const pickImage = (c) => {
    setForm((f) => ({ ...f, image_url: c.url, image_seed: c.seed }));
  };

  const toggleGame = (gid) => {
    setForm((f) => {
      const set = new Set(f.game_ids);
      if (set.has(gid)) set.delete(gid); else set.add(gid);
      return { ...f, game_ids: Array.from(set) };
    });
  };
  const toggleLevel = (lv) => {
    setForm((f) => {
      const set = new Set(f.levels);
      if (set.has(lv)) set.delete(lv); else set.add(lv);
      return { ...f, levels: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("O nome é obrigatório"); return; }
    if (!form.image_url) { toast.error("Escolhe uma imagem"); return; }
    if (Number(form.speed_min) > Number(form.speed_max)) { toast.error("Rapidez mínima > máxima"); return; }
    setSaving(true);
    const payload = {
      ...form,
      efficacy: Number(form.efficacy),
      speed_min: Number(form.speed_min),
      speed_max: Number(form.speed_max),
      mental_strength: Math.max(0, Math.min(10, Number(form.mental_strength) || 0)),
      game_ids: form.game_ids,
      levels: form.levels.map((x) => Number(x)),
      // Keep legacy field in sync (first game_id if any, else null) for back-compat.
      game_id: form.game_ids.length === 1 ? form.game_ids[0] : null,
    };
    try {
      if (robot) {
        await api.put(`/robots/${robot.id}`, payload);
      } else {
        await api.post("/robots", payload);
      }
      toast.success("Robot guardado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="robot-dialog" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-sky-500 to-sky-600 text-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            <h2 className="font-[Fredoka] text-xl font-bold">{robot ? "Editar Robot" : "Novo Robot"}</h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/15 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5">
          {/* Image picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Imagem do robot</Label>
              <Button type="button" onClick={regenerate} variant="outline" size="sm" disabled={loadingImgs} className="rounded-full" data-testid="robot-regen-images">
                <Wand2 className={`w-4 h-4 mr-1.5 ${loadingImgs ? "animate-spin" : ""}`} /> Gerar 3 novas
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {candidates.map((c) => (
                <button
                  key={c.seed} type="button" onClick={() => pickImage(c)}
                  className={`rounded-xl border-2 p-2 transition ${form.image_seed === c.seed ? "border-sky-500 ring-2 ring-sky-300 bg-sky-50" : "border-slate-200 hover:border-sky-300"}`}
                  data-testid={`robot-img-candidate-${c.seed}`}
                >
                  <img src={c.url} alt={c.seed} className="w-full aspect-square object-contain" />
                </button>
              ))}
            </div>
            {form.image_url && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-2 flex items-center gap-3 mt-2" data-testid="robot-img-selected">
                <img src={form.image_url} alt="atual" className="w-14 h-14 rounded-lg object-contain bg-white border" />
                <div className="text-xs text-emerald-800"><b>Imagem atual selecionada.</b><br />Clica em "Gerar 3 novas" para escolher outra.</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TranslatedInput
              label="Nome*"
              field="name"
              value={form.name}
              translations={form.translations}
              onChange={({ value, translations }) => setForm({ ...form, name: value, translations })}
              placeholder="ZEUS-BOT"
              testid="robot-name"
            />
            <TranslatedInput
              label="Alcunha"
              field="alcunha"
              value={form.alcunha}
              translations={form.translations}
              onChange={({ value, translations }) => setForm({ ...form, alcunha: value, translations })}
              placeholder="o relâmpago"
              testid="robot-alcunha"
            />
          </div>

          <div>
            <Label>Eficácia · {form.efficacy}% de respostas certas</Label>
            <input
              type="range" min={0} max={100} step={1} value={form.efficacy}
              onChange={(e) => setForm({ ...form, efficacy: Number(e.target.value) })}
              className="w-full accent-sky-500" data-testid="robot-efficacy"
            />
            <div className="flex justify-between text-[10px] text-slate-500 uppercase font-bold mt-0.5">
              <span>fraco</span><span>médio</span><span>perfeito</span>
            </div>
          </div>

          <div>
            <Label>Rapidez — intervalo de resposta (segundos)</Label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input type="number" min={0} max={60} step="0.1" value={form.speed_min} onChange={(e) => setForm({ ...form, speed_min: e.target.value })} data-testid="robot-speed-min" />
                <div className="text-[10px] text-slate-500 mt-1">Mínimo</div>
              </div>
              <span className="text-slate-400">→</span>
              <div className="flex-1">
                <Input type="number" min={0} max={60} step="0.1" value={form.speed_max} onChange={(e) => setForm({ ...form, speed_max: e.target.value })} data-testid="robot-speed-max" />
                <div className="text-[10px] text-slate-500 mt-1">Máximo</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Em cada questão, o robot responde num tempo aleatório entre <b>{form.speed_min}s</b> e <b>{form.speed_max}s</b>.</p>
          </div>

          <div>
            <Label className="flex items-center gap-1.5"><Brain className="w-4 h-4 text-violet-500" /> Força Mental · <span className="text-violet-700 font-bold">{form.mental_strength}/10</span></Label>
            <input
              type="range" min={0} max={10} step={1} value={form.mental_strength}
              onChange={(e) => setForm({ ...form, mental_strength: Number(e.target.value) })}
              className="w-full accent-violet-500" data-testid="robot-mental-strength"
            />
            <div className="flex justify-between text-[10px] text-slate-500 uppercase font-bold mt-0.5">
              <span>iniciante</span><span>desafiante</span><span>imbatível</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Pontos no ranking: <b className="text-emerald-600">vitória +{2 * form.mental_strength}</b>,
              <b className="text-rose-600 ml-1">derrota −{form.mental_strength / 2}</b>.
            </p>
          </div>

          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">Jogos disponíveis <span className="text-slate-400 text-xs">(deixar vazio = todos)</span></Label>
            <p className="text-[11px] text-slate-400 mb-1.5">
              O modo de cada jogo é indicado por baixo do nome. <strong>VS Robot</strong> só mostra jogos de <strong>treino</strong>; para o robot aparecer nesse jogo, escolhe a versão de <strong>treino</strong> (jogos com o mesmo nome podem ser de modos diferentes).
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="robot-game-ids">
              {games.length === 0 ? (
                <span className="text-xs text-slate-400">Sem jogos configurados</span>
              ) : [...games]
                .sort((a, b) => (a.name || "").localeCompare(b.name || "") || (a.status || "").localeCompare(b.status || ""))
                .map((g) => {
                const selected = form.game_ids.includes(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => toggleGame(g.id)}
                    className={`px-2.5 py-1 rounded-xl text-xs font-bold border-2 transition flex flex-col items-start leading-tight ${selected ? "bg-sky-500 text-white border-sky-500" : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"}`}
                    data-testid={`robot-game-${g.id}`}
                  >
                    <span>{g.name}</span>
                    <span className={`text-[9px] font-semibold tracking-wide ${selected ? "text-sky-100" : (g.status === "training" ? "text-emerald-600" : "text-slate-400")}`}>
                      {gameModeLabel(g.status)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-start">
            <div>
              <Label className="flex items-center gap-1.5 mb-1.5">Níveis disponíveis <span className="text-slate-400 text-xs">(vazio = todos)</span></Label>
              <div className="flex flex-wrap gap-1.5" data-testid="robot-levels">
                {[1, 2, 3, 4, 5].map((lv) => {
                  const selected = form.levels.includes(lv);
                  return (
                    <button
                      key={lv}
                      type="button"
                      onClick={() => toggleLevel(lv)}
                      className={`w-9 h-9 rounded-full text-sm font-bold border-2 transition ${selected ? "bg-sky-500 text-white border-sky-500" : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"}`}
                      data-testid={`robot-level-${lv}`}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-3 pb-1 self-end">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="robot-active" />
              <Label>Ativo</Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="robot-save">
              <Save className="w-4 h-4 mr-2" /> {saving ? "A guardar…" : "Guardar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

